import { StripeCardElementChangeEvent, StripeError } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';

import { APIResponse } from '@alluradirect/react';
import { useStripePayment, UseStripePaymentArgs } from 'hooks/stripe/useStripePayment';
import { handleOnErrorToast } from 'helpers/toastHelpers';

export type HandleStripeSubmission = ({
  endpoint,
  onSuccess,
  onStripeError,
  additionalSendData,
  sendWithoutCardData,
}: {
  endpoint: string;
  onSuccess?: (data?: APIResponse<any>) => void;
  onStripeError?: (data?: APIResponse<any>) => void;
  additionalSendData?: any;
  sendWithoutCardData?: boolean;
}) => void;

interface Args {
  initialCardholderName?: string;
}

/** Use this hook with the StripeField component,
 * pass props to that component from this hook.
 *
 * Use handleStripeSubmission to make a payment
 */
export function useStripeField(args: UseStripePaymentArgs & Args = {}) {
  const { makeStripePayment, isAlluraServerError, isLoading } = useStripePayment(args);

  const [hasCardholderNameError, setHasCardholderNameError] = useState(false);
  const [cardholderName, setCardholderName] = useState(args.initialCardholderName ?? '');

  const [isCardComplete, setIsCardComplete] = useState(false);
  const [cardError, setCardError] = useState<StripeError | null>(null);
  const [alluraServerApiError, setAlluraServerApiError] = useState<string | null>(null);
  const [stripeApiError, setStripeApiError] = useState<undefined | string>(undefined);

  const isComplete = isCardComplete && cardholderName.length > 0;

  const handleStripeSubmission: HandleStripeSubmission = ({
    endpoint,
    onSuccess = _data => null,
    onStripeError,
    additionalSendData,
    sendWithoutCardData = false,
  }) => {
    if (!isComplete && !sendWithoutCardData) {
      setCardError({
        type: 'validation_error',
        message: 'Please fill in name and credit card details.',
      });

      return;
    }

    setAlluraServerApiError(null);
    setStripeApiError(undefined);

    const cardHolder = {
      name: cardholderName,
    };

    const handleStripeError = (data: APIResponse<any>) => {
      setStripeApiError(data?.ERRORS?.CLIENT_MESSAGE);
      handleOnErrorToast(new Error(JSON.stringify(data)));
      onStripeError && onStripeError(data);
    };

    makeStripePayment({
      cardHolder,
      endpoint,
      onSuccess,
      onStripeError: handleStripeError,
      additionalSendData,
      sendWithoutCardData,
    });
  };

  const onChangeCard = ({ error, complete }: StripeCardElementChangeEvent) => {
    if (error) {
      setCardError(error);
    }
    if (!error && cardError) {
      setCardError(null);
    }
    setIsCardComplete(complete);
  };

  const onChangeCardholderName = (e: any) => {
    const val = e.target.value;

    if (val.length === 0) {
      setHasCardholderNameError(true);
    } else {
      setHasCardholderNameError(false);
    }
    setCardholderName(val);
  };

  useEffect(() => {
    if (isAlluraServerError) {
      setAlluraServerApiError('Payment Failed, Please Try Again.');
    }
  }, [isAlluraServerError]);

  return {
    isLoading,
    isComplete,
    cardError,
    stripeApiError,
    alluraServerApiError,
    hasCardholderNameError,
    handleStripeSubmission,
    onChangeCard,
    cardholderName,
    onChangeCardholderName,
  };
}

export type UseStripeField = ReturnType<typeof useStripeField>;
